import { ModuleTile } from "@/components/ModuleTile";
import { StudyLogo } from "@/components/StudyLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { studyApi, type Study } from "@/services/studyApi";
import { ResourceCard } from "@/components/ResourceCard";
import { StatusBadge, type StatusTone } from "@/components/StatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { FoundryCardGridSkeleton, ModuleTileGridSkeleton } from "@/components/skeletons";
import { toast } from "sonner";
import {
  TrendingUp,
  Package,
  Factory,
  Calendar,
  Boxes,
  DollarSign,
  Building2,
  FileSpreadsheet,
  Database,
  BarChart3,
  Search,
  Sparkles,
  ShoppingCart,
  Truck,
  Target,
  Users,
  Clock,
  Eye,
  Copy,
  Trash2,
  ChevronRight,
  MoreVertical,
} from "lucide-react";

const IconMap: Record<string, any> = {
  TrendingUp,
  Package,
  ShoppingCart,
  Factory,
  Calendar,
  Boxes,
  Target,
  DollarSign,
  Building2,
  FileSpreadsheet,
  BarChart3,
  Users,
  Truck,
  Sparkles,
  Database,
};

// Study templates will be fetched from the API

// A study is "in-flight" when its latest execution is queued or running —
// deleting it would orphan a forecast-svc job whose callback would 404 on the
// way back. The BE refuses too; we mirror it on the FE so the checkbox is
// disabled rather than letting the user select-then-fail.
const isInFlight = (s: Study) =>
  s.latest_execution?.status === "queued" ||
  s.latest_execution?.status === "running";

// Study lifecycle status → badge tone. Anything the BE adds later
// falls back to neutral rather than a filled primary pill.
const STUDY_STATUS_TONE: Record<string, StatusTone> = {
  completed: "success",
  running: "info",
  queued: "neutral",
  draft: "neutral",
  failed: "destructive",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Study | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: studies = [], isLoading: studiesLoading } = useQuery({
    queryKey: ["studies"],
    queryFn: studyApi.list,
    refetchOnWindowFocus: false
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["study-templates"],
    queryFn: studyApi.listTemplates,
    refetchOnWindowFocus: false
  });

  const handleModuleClick = (templateId: string, route: string) => {
    // Navigate with template reference
    navigate(`${route}?templateId=${templateId}`);
  };

  // Render a template card with disabled-state handling.
  // `enabled_for_tenant` comes from the BE; defaults to true when not set.
  const renderTemplateTile = (
    template: any,
    index: number,
    category: "Sales" | "Operations" | "Finance" | "Revenue" | "Data",
  ) => {
    const FallbackIcon = IconMap[template.icon || "Sparkles"] || Sparkles;
    const enabled = template.enabled_for_tenant !== false;
    return (
      <ModuleTile
        key={template.id}
        title={template.name}
        description={template.description}
        category={category}
        disabled={!enabled}
        // StudyLogo renders a hand-crafted SVG when the study key is
        // in its registry (currently 13 keys), else falls back to the
        // Lucide icon the BE template hints at. We pass the rendered
        // Lucide element (not the icon name) so the bundler can tree-
        // shake lucide-react instead of pulling in every icon for a
        // string→component lookup.
        icon={
          <StudyLogo
            studyKey={template.key}
            fallback={<FallbackIcon className="w-5 h-5" />}
            size={20}
          />
        }
        onClick={
          enabled ? () => handleModuleClick(template.id, template.route) : undefined
        }
        className="animate-scale-in"
        style={{ animationDelay: `${index * 50}ms` }}
      />
    );
  };

  // Opens the confirm dialog; the actual delete runs in confirmDeleteStudy.
  const handleDeleteStudy = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteTarget(studies.find((s) => s.id === id) ?? null);
  };

  const confirmDeleteStudy = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await studyApi.delete(deleteTarget.id);
      toast.success("Study deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      setDeleteTarget(null);
    } catch (err) {
      toast.error("Failed to delete study");
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  // Drop selections that point at studies that no longer exist (e.g. after
  // refetch following a delete) so the action bar count stays honest.
  const selectableStudies = useMemo(
    () => studies.filter((s) => !isInFlight(s)),
    [studies],
  );
  const liveSelected = useMemo(
    () => selectableStudies.filter((s) => selectedIds.has(s.id)),
    [selectableStudies, selectedIds],
  );
  const allSelected =
    selectableStudies.length > 0 &&
    liveSelected.length === selectableStudies.length;
  const someSelected =
    liveSelected.length > 0 && liveSelected.length < selectableStudies.length;

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(selectableStudies.map((s) => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const ids = liveSelected.map((s) => s.id);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await studyApi.bulkDelete(ids);
      const skippedRunning = res.skipped.filter((s) => s.reason === "running").length;
      if (res.deleted.length && skippedRunning) {
        toast.success(
          `Deleted ${res.deleted.length}; skipped ${skippedRunning} still running`,
        );
      } else if (res.deleted.length) {
        toast.success(`Deleted ${res.deleted.length} studies`);
      } else {
        toast.error("No studies were deleted");
      }
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["studies"] });
    } catch (err) {
      toast.error("Bulk delete failed");
      console.error("Bulk delete error:", err);
    } finally {
      setBulkDeleting(false);
      setBulkConfirmOpen(false);
    }
  };

  const filteredTemplates = (categoryFilter: string) => {
    let visibleTemplates = templates;
    if (categoryFilter !== 'all') {
      visibleTemplates = templates.filter(t => t.category === categoryFilter);
    }

    if (!searchQuery) return visibleTemplates;
    return visibleTemplates.filter(t =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Select your Study"
        subtitle="All studies are powered by the AI Insight Engine. Add your data — our models handle the rest."
      />

      {/* Main Content */}
      <div className="p-6 space-y-6">

        {/* Modern Tabs and Search */}
        <div className="flex items-center justify-between">
          <Tabs defaultValue="supply-chain" className="w-full">
            <div className="flex items-center justify-between mb-6">
              {/* Finance / Pricing / ESG tabs are temporarily hidden — those
                  templates exist in the BE but their FE flows aren't ready
                  yet. Restore by re-adding the TabsTrigger entries below
                  (the matching TabsContent blocks are still in the file
                  in case any are linked to deep). */}
              <TabsList>
                <TabsTrigger
                  value="supply-chain">
                  Supply Chain
                </TabsTrigger>
                <TabsTrigger
                  value="past-studies">
                  Past Studies
                </TabsTrigger>
              </TabsList>

              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search All Studies"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Modern Tab Contents */}
            <TabsContent value="supply-chain" className="mt-0">
              {templatesLoading ? (
                <ModuleTileGridSkeleton />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredTemplates("Supply Chain").map((t, i) => renderTemplateTile(t, i, "Operations"))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="finance" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTemplates("Finance").map((t, i) => renderTemplateTile(t, i, "Finance"))}
              </div>
            </TabsContent>

            <TabsContent value="pricing" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTemplates("Pricing").map((t, i) => renderTemplateTile(t, i, "Revenue"))}
              </div>
            </TabsContent>

            <TabsContent value="esg" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTemplates("ESG").map((t, i) => renderTemplateTile(t, i, "Data"))}
              </div>
            </TabsContent>

            <TabsContent value="past-studies" className="mt-0 space-y-3">
              {studies.length > 0 && (
                <div className="flex items-center justify-between rounded-md border bg-card px-4 py-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleAll(v === true)}
                      aria-label="Select all studies"
                      disabled={selectableStudies.length === 0}
                    />
                    <span className="text-muted-foreground">
                      {liveSelected.length > 0
                        ? `${liveSelected.length} selected`
                        : `Select all (${selectableStudies.length} eligible)`}
                    </span>
                  </label>
                  {liveSelected.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setBulkConfirmOpen(true)}
                      disabled={bulkDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {liveSelected.length}
                    </Button>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {studiesLoading ? (
                  <div className="col-span-full">
                    <FoundryCardGridSkeleton count={3} />
                  </div>
                ) : studies.length === 0 ? (
                  <div className="col-span-full">
                    <EmptyState
                      icon={Database}
                      title="No studies yet"
                      body="Create your first study from the Supply Chain catalog to get started."
                    />
                  </div>
                ) : (
                  studies.map((study, index) => {
                    const inFlight = isInFlight(study);
                    const checked = selectedIds.has(study.id);
                    // Study-type glyph — falls back to the template's
                    // Lucide icon when the study type isn't in the
                    // StudyLogo registry.
                    const HintedIcon = study.template_info?.icon
                      ? (IconMap[study.template_info.icon] || Sparkles)
                      : Database;
                    // Route to the study's own page (driven by
                    // template_info.route). Falls back to a slug built
                    // from study.study_type for studies whose template
                    // isn't registered.
                    const openResults = () => {
                      const route = study.template_info?.route || `/${study.study_type}`;
                      navigate(`${route}?studyId=${study.id}&step=4`);
                    };
                    const lastRun = study.latest_execution?.status;
                    return (
                      <ResourceCard
                        key={study.id}
                        className="animate-scale-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                        selected={checked}
                        onClick={openResults}
                        leading={
                          <Checkbox
                            checked={checked}
                            disabled={inFlight}
                            onCheckedChange={(v) => toggleOne(study.id, v === true)}
                            aria-label={
                              inFlight
                                ? `Cannot select ${study.name} — still running`
                                : `Select ${study.name}`
                            }
                          />
                        }
                        icon={
                          <StudyLogo
                            studyKey={study.study_type}
                            fallback={<HintedIcon />}
                            size={18}
                          />
                        }
                        eyebrow={study.study_type.replace(/-/g, " ")}
                        title={study.name}
                        description={study.description || "No description provided."}
                        badges={
                          <StatusBadge
                            tone={STUDY_STATUS_TONE[study.status] ?? "neutral"}
                            className="capitalize"
                          >
                            {study.status}
                          </StatusBadge>
                        }
                        menu={
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground"
                                aria-label="Study actions"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={openResults}>
                                <Eye className="h-4 w-4 mr-2" /> View results
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Copy className="h-4 w-4 mr-2" /> Clone study
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={inFlight}
                                onClick={(e) => handleDeleteStudy(e, study.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        }
                        meta={[
                          {
                            label: "Created",
                            value: new Date(study.created_at).toLocaleDateString(),
                          },
                          {
                            label: "Metrics",
                            value: study.latest_execution?.metrics ? "Available" : "—",
                          },
                        ]}
                        footerStart={
                          <>
                            <Clock className="h-3.5 w-3.5" />
                            <span className="capitalize">
                              {lastRun ? `Last run ${lastRun}` : "Not run yet"}
                            </span>
                          </>
                        }
                        footer={
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-primary hover:text-primary"
                            onClick={openResults}
                          >
                            View results
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        }
                      />
                    );
                  })
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the study, its execution history and
              any associated result files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteStudy}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {liveSelected.length} {liveSelected.length === 1 ? "study" : "studies"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected studies, their execution
              history, and any associated result files. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}